package dev.lingvo.shared.ops

import dev.lingvo.shared.MtProvider
import dev.lingvo.shared.Plan
import dev.lingvo.shared.PlanStatus
import dev.lingvo.shared.Role
import kotlinx.serialization.Serializable

/** What went wrong with a machine-translation run, as a closed set. */
@Serializable
enum class OpsFailureClass {
    NONE,
    PROVIDER_AUTH,
    PROVIDER_RATE_LIMIT,
    PROVIDER_UNAVAILABLE,
    PROVIDER_REJECTED,
    KEY_UNREADABLE,
    OUT_OF_CREDITS,
    SOURCE_TOO_LONG,
    LEASE_LAPSED,
    UNCLASSIFIED,
}

/** A run's state as an operator needs to read it. */
@Serializable
enum class OpsJobState { QUEUED, RUNNING, ABANDONED, DONE, FAILED }

/** What a search hit is. A closed list: search never guesses at an unapproved table. */
@Serializable
enum class OpsHitKind { PERSON, WORKSPACE, PROJECT, JOB, BILLING_DOCUMENT }

/** A server-composed search result with no customer-supplied URL. */
@Serializable
data class OpsSearchHitDto(
    val kind: OpsHitKind,
    val id: String,
    val title: String,
    val subtitle: String,
    val matchedOn: String,
    val href: String,
)

/** One row of the cross-tenant workspace directory. */
@Serializable
data class OpsWorkspaceRowDto(
    val orgId: String,
    val name: String,
    val slug: String,
    val plan: Plan,
    val planStatus: PlanStatus,
    val members: Int,
    val projects: Int,
    val ownerless: Boolean,
    val createdAt: String,
)

/** One row of the cross-tenant user directory. */
@Serializable
data class OpsUserRowDto(
    val userId: String,
    val name: String,
    val email: String,
    val workspaces: Int,
    val verified: Boolean,
    val closingAt: String?,
    val createdAt: String,
)

@Serializable
data class OpsWorkspaceDto(
    val orgId: String,
    val name: String,
    val slug: String,
    val plan: Plan,
    val planStatus: PlanStatus,
    val createdAt: String,
    val renewsAt: String?,
    val hasSubscription: Boolean,
    val subscriptionCount: Int,
    val hasCustomer: Boolean,
    val seatsGranted: Int,
    val seatsBilled: Int?,
    val seatsSyncedAt: String?,
    val seatSyncGraceSeconds: Int,
    val ownerless: Boolean,
    val ownerCount: Int,
)

@Serializable
data class OpsMemberRowDto(
    val userId: String,
    val name: String,
    val email: String,
    val role: Role,
    val canApprove: Boolean?,
    val joinedAt: String,
    val lastActiveBucket: String,
)

@Serializable
data class OpsProjectRowDto(
    val projectId: String,
    val name: String,
    val slug: String,
    val sourceLocale: String,
    val locales: Int,
    val keys: Int,
    val createdAt: String,
)

@Serializable
data class OpsInvitationRowDto(
    val id: String,
    val email: String,
    val role: Role,
    val invitedBy: String?,
    val createdAt: String,
    val expiresAt: String,
    val acceptedAt: String?,
    val revokedAt: String?,
)

@Serializable
data class OpsGitHubDto(
    val installed: Boolean,
    val installationId: String?,
    val connectedAt: String?,
    val installedBy: String?,
)

@Serializable
data class OpsDeletionScopeDto(
    val projects: Int,
    val keys: Int,
    val translations: Int,
    val members: Int,
    val tokens: Int,
    val webhooks: Int,
    val glossaryTerms: Int,
)

@Serializable
data class OpsProviderRowDto(
    val provider: MtProvider,
    val enabled: Boolean,
    val hasKey: Boolean,
    val keyUnreadable: Boolean,
    val baseUrl: Untrusted?,
    val testedOkAt: String?,
)

@Serializable
data class OpsProviderOverrideDto(
    val projectId: String,
    val projectName: String,
    val provider: MtProvider,
    val mode: String,
    val hasKey: Boolean,
    val keyUnreadable: Boolean,
)

@Serializable
data class OpsProviderListDto(
    val verdict: String,
    val unreadableKeyCount: Int,
    val workspace: List<OpsProviderRowDto>,
    val projectOverrides: List<OpsProviderOverrideDto>,
    /** Presence only, one entry per provider. Never a key, never a hint. */
    val platformKeys: Map<String, Boolean>,
)

@Serializable
data class OpsBillingDto(
    val plan: Plan,
    val planStatus: PlanStatus,
    val renewsAt: String?,
    val hasSubscription: Boolean,
    val subscriptionCount: Int,
    val hasCustomer: Boolean,
    val creditsIncluded: Int,
    val creditsGranted: Int,
    val creditsUsed: Int,
    val periodStart: String,
    val ledgerBegins: String,
    val grantHeadroom: Int,
    val agentBudgetFloor: OpsAgentBudgetDto?,
)

@Serializable
data class OpsCreditEventRowDto(
    val id: String,
    val kind: String,
    val credits: Int,
    val strings: Int,
    val projectName: String?,
    val locale: String?,
    val provider: String?,
    val jobId: String?,
    val createdAt: String,
)

@Serializable
data class OpsGrantRowDto(
    val id: String,
    val credits: Int,
    val period: String,
    val reason: String,
    val grantedBy: String?,
    val createdAt: String,
)

@Serializable
data class OpsCheckoutRowDto(
    val id: String,
    val plan: Plan,
    val seats: Int,
    val createdBy: String?,
    val createdAt: String,
    val settledAt: String?,
    val ageSeconds: Long,
)

@Serializable
data class OpsCancellationRowDto(
    val id: String,
    val plan: Plan,
    val reason: String?,
    val comment: Untrusted?,
    val cancelledBy: String?,
    val effectiveAt: String?,
    val createdAt: String,
)

@Serializable
data class OpsPaddleEventRowDto(
    val eventId: String,
    val eventType: String,
    val occurredAt: String?,
    val receivedAt: String,
    val linked: Boolean,
    val summary: String,
)

@Serializable
data class OpsSeatDivergenceRowDto(
    val orgId: String,
    val name: String,
    val slug: String,
    val seatsGranted: Int,
    val seatsBilled: Int,
    val syncedAt: String?,
    val staleSeconds: Long?,
)

@Serializable
data class OpsMtJobRowDto(
    val jobId: String,
    val orgId: String?,
    val orgName: String?,
    val projectSlug: String?,
    val provider: MtProvider,
    val locales: List<String>,
    val state: OpsJobState,
    val failureClass: OpsFailureClass,
    val hadEarlierFailure: Boolean,
    val total: Int,
    val done: Int,
    val failed: Int,
    val rejected: Int,
    val reservedCredits: Int,
    val refundedCredits: Int?,
    val reservedPeriod: String?,
    val startedBy: String?,
    val createdAt: String,
    val finishedAt: String?,
    val refundedAt: String?,
)

@Serializable
data class OpsMtLeaseDto(
    val workerId: String?,
    val heartbeatAt: String?,
    val heartbeatAgeSeconds: Long?,
    val leaseSeconds: Int,
)

@Serializable
data class OpsJobDetailDto(
    val job: OpsMtJobRowDto,
    val lease: OpsMtLeaseDto?,
    val forceFail: OpsForceFailPreviewDto? = null,
)

@Serializable
data class OpsCronHealthDto(
    val jobs: List<OpsCronRowDto>,
    val growth: List<OpsGrowthRowDto>,
)

@Serializable
data class OpsJobHealthDto(
    val failedSince: Int,
    val sinceHours: Int,
    val recent: List<OpsMtJobRowDto>,
    val unrefundedAbandoned: List<OpsMtJobRowDto>,
    val cappedAt: Int,
)

@Serializable
data class OpsSeatHealthDto(
    val rows: List<OpsSeatDivergenceRowDto>,
    val graceSeconds: Long,
    val cappedAt: Int,
)

@Serializable
data class OpsProbeDto(
    val name: String,
    val ok: Boolean,
    val reason: String? = null,
)

@Serializable
data class OpsSiteHealthDto(
    val probes: List<OpsProbeDto>,
    val probeSourceReachable: Boolean,
    val checkedAt: String,
    val note: String,
)

@Serializable
data class OpsCronRowDto(
    val job: String,
    val schedule: String,
    val thresholdSeconds: Long,
    val arrivesInPhase: Int,
    val runnable: Boolean,
    val shippedThroughPhase: Int,
    val lastSuccessAt: String?,
    val affected: Int,
    val everRun: Boolean,
    val lateBySeconds: Long?,
)

@Serializable
data class OpsGrowthRowDto(val table: String, val estimatedRows: Long)

@Serializable
data class OpsWebhookRowDto(
    val webhookId: String,
    val orgId: String,
    val orgName: String,
    val url: Untrusted,
    val events: List<String>,
    val enabled: Boolean,
    val signed: Boolean,
    val consecutiveFailures: Int,
    val lastStatus: Int?,
    val lastAttemptAt: String?,
    val lastSuccessAt: String?,
    val createdAt: String,
)

@Serializable
data class OpsDeliveryRowDto(
    val deliveryId: String,
    val webhookId: String,
    val event: String,
    val attempts: Int,
    val succeeded: Boolean,
    val status: Int?,
    val responseBody: Untrusted?,
    val bodyBytes: Int,
    val createdAt: String,
    val finishedAt: String?,
)

@Serializable
enum class OpsSentinelState { VERIFIED, FAILED, UNTESTED }

@Serializable
data class OpsProviderKeyHealthDto(
    val sentinel: OpsSentinelState,
    val plantedAt: String?,
    val encryptedRows: Long,
    val unreadableKeys: Long,
)


@Serializable
data class OpsUserDto(
    val userId: String,
    val name: String,
    val email: String,
    val verified: Boolean,
    val hasPassword: Boolean,
    val closingAt: String?,
    val createdAt: String,
    val liveCredentials: OpsLiveCredentialsDto,
)

@Serializable
data class OpsUserWorkspaceRowDto(
    val orgId: String,
    val name: String,
    val slug: String,
    val role: Role,
    val canApprove: Boolean?,
    val soleOwner: Boolean,
    val plan: Plan,
    val joinedAt: String,
    val lastActiveBucket: String,
)

@Serializable
enum class OpsCredentialKind { SESSION, API_TOKEN, AGENT, OAUTH_IDENTITY }

@Serializable
data class OpsCredentialRowDto(
    val kind: OpsCredentialKind,
    val id: String,
    val name: Untrusted?,
    val scopes: List<String>,
    val orgSlug: String?,
    val createdAt: String,
    val lastSeenBucket: String,
    val expiresAt: String?,
    val revokedAt: String?,
)

@Serializable
data class OpsEmailAttemptRowDto(
    val id: String,
    val kind: String,
    val recipient: String,
    val subject: String,
    val delivered: Boolean,
    val createdAt: String,
)

@Serializable
data class OpsTokenMintRowDto(
    val kind: String,
    val createdAt: String,
    val expiresAt: String?,
    val consumedAt: String?,
)

@Serializable
data class OpsSearchDto(
    val query: String,
    val hits: List<OpsSearchHitDto>,
    val exactOnly: Boolean,
    val searched: List<String>,
    val notSearched: List<String>,
)
